import Tooltip from '@atlaskit/tooltip';
import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import words from 'lodash/words';
import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import { formatSen, getEntitlementRoute } from '@atlassian/cssxp-utils-entitlement';
import { isBitbucketCloud, isOpsgenie } from '@atlassian/cssxp-utils-product';
import { CloudSubscription, SourceSystem } from '@atlassian/cssxp-typescommon';
import { getEntitlementName } from '@atlassian/temp-helper';
import { Disabled } from '../../security/src/containers/feature-switch';
import { getEnv } from '../../../../utils/client-utils/env';
import CloudSubscriptionChildProductRow from './cloud-subscription-child-product-row';
import EntitlementIcon from '../../entitlement-icon/entitlement-icon';

interface Props extends CloudSubscription {
  includeRoles: boolean;
  transactionDetail?: {
    billToCompany: string;
    shipToCompany: string;
    transactionAccountId: string;
  };
  newURL?: boolean;
}

const RoleMap = {
  SITE_ADMIN: 'Site Admin',
  USER: 'User',
};

const getUserRoles = (roles: Array<'USER' | 'SITE_ADMIN'>) =>
  roles
    .filter((role, _, allRoles) => {
      // SITE_ADMIN implies they're a user, so we only need to show SITE_ADMIN
      if (role === 'USER' && allRoles.includes('SITE_ADMIN')) {
        return false;
      }

      return true;
    })
    .map((role) => RoleMap[role])
    .sort();

export default function CloudSubscriptionRow(props: Props) {
  const {
    id,
    productId,
    billingStatus,
    sen,
    entitlementNumber,
    includeRoles,
    isCloud,
    licenseLevel,
    latestPeriodEndDate,
    renewalAction,
    billingCycle,
    baseProduct = '',
    parentSen,
    roles,
    edition,
    unitCount,
    sourceSystem,
    childEntitlements = {
      totalHits: 0,
      results: [],
    },
    pageName,
    transactionDetail,
    newURL,
  } = props;

  const name = getEntitlementName(props);

  // Function to assign url based on the environment
  const getUrl = () => {
    return `/cloud-sites/${sen}`;
  };

  // Get the navigation link based on source system
  const link = getEntitlementRoute(props, newURL);
  const linkBasedOnSourceSystem = sourceSystem !== SourceSystem.CCP ? getUrl() : getEntitlementRoute(props, newURL);

  const navigate = (link: string) => {
    const url = window.location.assign(`${link}`);

    return url;
  };

  const tier =
    unitCount &&
    unitCount !== 'Unlimited' &&
    unitCount > 0 &&
    !['Atlassian Cloud', 'Atlassian Access'].includes(baseProduct)
      ? `${unitCount} users`
      : '';

  const formattedSen = formatSen(sen);

  const renewal = isCloud ? capitalize(billingCycle) : words(capitalize(renewalAction)).join('-');

  const date = moment(latestPeriodEndDate || new Date()).format('D MMM YYYY');

  const maxChildrenVisible = 6;

  const totalChildren = childEntitlements.totalHits;

  const children = childEntitlements.results.slice(0, maxChildrenVisible);

  const hiddenChildrenCount = totalChildren - children.length;

  const childRowColSpan = includeRoles ? 6 : 5;

  const isFreeBitbucketCloud = isBitbucketCloud(productId) && licenseLevel === 'Free';

  const isFreeOpsGenie = isOpsgenie(productId) && edition === 'Free';

  const hasValidBillingValues = !isFreeBitbucketCloud && !isFreeOpsGenie;

  const optionalTooltips = [
    {
      predicate: billingStatus === 'SUSPENDED',
      description: 'Deactivated',
    },
    {
      predicate: billingStatus === 'DELETED',
      description: 'Deleted',
    },
  ];

  const optionalTooltip = optionalTooltips.find((tooltip) => tooltip.predicate === true);

  return (
    <>
      <tr
        key={`cloud-subscription-${id}`}
        data-testid={`cloud-subscription-${id}`}
        className={classNames('search-result', {
          'with-child-results': children.length > 0,
          deactivated: billingStatus === 'SUSPENDED',
          deleted: billingStatus === 'DELETED',
        })}
        onClick={() => navigate(linkBasedOnSourceSystem)}
      >
        <td title={name} className="entitlement-name">
          {optionalTooltip !== undefined ? (
            <Tooltip position="bottom" content={optionalTooltip.description}>
              {sourceSystem === SourceSystem.CCP ? (
                <Link
                  to={link}
                  onClick={(e) => e.preventDefault()}
                  className="avatar-container"
                  data-testId="toolTip-name-link"
                >
                  <EntitlementIcon productId={productId} className="avatar medium" isCloud />
                  {!!name && ` ${name}`}
                </Link>
              ) : (
                <div onClick={() => navigate(linkBasedOnSourceSystem)} data-testId="toolTip-Icon-Container">
                  <EntitlementIcon productId={productId} className="avatar medium" isCloud />
                  <a onClick={() => navigate(linkBasedOnSourceSystem)}>{!!name && ` ${name}`}</a>
                </div>
              )}
            </Tooltip>
          ) : (
            <>
              {sourceSystem === SourceSystem.CCP ? (
                <Link
                  to={link}
                  onClick={(e) => e.preventDefault()}
                  className="avatar-container"
                  data-testId="name-link"
                >
                  <EntitlementIcon productId={productId} className="avatar medium" isCloud />
                  {!!name && ` ${name}`}
                </Link>
              ) : (
                <div onClick={() => navigate(linkBasedOnSourceSystem)} data-testId="Icon-Container">
                  <EntitlementIcon productId={productId} className="avatar medium" isCloud />
                  <a onClick={() => navigate(linkBasedOnSourceSystem)}>{!!name && ` ${name}`}</a>
                </div>
              )}
            </>
          )}
        </td>
        <td className="entitlement-user-tier">
          <span data-testid="entitlement-user-tier">{tier}</span>
        </td>
        <td className="cloud-subscription-role" data-testid="cloud-subscription-role">
          {roles &&
            getUserRoles(roles).map((role) => (
              <React.Fragment key={role}>
                {role}
                <br />
              </React.Fragment>
            ))}
        </td>
        <td className="entitlement-sen" data-testid="entitlement-sen-cell">
          <Link to={link} style={{ textDecoration: 'none', color: 'inherit' }} data-testid="entitlement-sen-link">
            {sourceSystem !== SourceSystem.CCP ? formattedSen : entitlementNumber}
          </Link>
        </td>
        <td title={pageName}>{pageName}</td>
        <td className="entitlement-renewal">
          <span
            className={classNames({
              'muted-status': !hasValidBillingValues,
            })}
          >
            {hasValidBillingValues ? renewal : 'n/a'}
          </span>
        </td>
        <td className="entitlement-end-date">
          <span
            className={classNames({
              'muted-status': !hasValidBillingValues,
            })}
          >
            {hasValidBillingValues ? date : 'n/a'}
          </span>
        </td>
        <td className="entitlement-edition">
          <span data-testid="enttitlement-edition">{edition}</span>
        </td>
        <td className="entitlement-billToCompany">
          <span data-testid="entitlement-billToCompany">{transactionDetail?.billToCompany}</span>
        </td>
        <td className="entitlement-shipToCompany">
          <span data-testid="entitlement-shipToCompany">{transactionDetail?.shipToCompany}</span>
        </td>
      </tr>
      {/* <FeatureSwitch flag={HIDE_CLOUD_CHILD_PRODUCTS}> */}
      <Disabled>
        {children.map((child) => (
          <CloudSubscriptionChildProductRow
            key={child.sen}
            {...child}
            colSpan={childRowColSpan}
            isParentDeactivated={billingStatus === 'SUSPENDED'}
          />
        ))}
        {totalChildren > maxChildrenVisible && (
          <tr className="search-result-child">
            <td colSpan={childRowColSpan} style={{ paddingLeft: '44px' }}>
              + {hiddenChildrenCount} more
            </td>
          </tr>
        )}
      </Disabled>
      {/* </FeatureSwitch> */}
    </>
  );
}
